import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth";
import { observer } from "mobx-react-lite";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3 } from "lucide-react";
import { PATHS } from "@/lib/router";
import { formatYear, generateYearOptions } from "../utils/year-helpers";
import { useOrganizationStore } from "@/features/organization";
import { useCarbonBankSubmissionsStore } from "../hooks";
import { useTran } from "@/hooks";
import OrgFlatChecklist from "../components/dashboard/OrgFlatChecklist";
import PaginationControls from "../components/submissions/PaginationControls";

const consentBadge = (status) => {
  if (status === "approved")
    return <Badge className="bg-emerald-600 text-white">approved</Badge>;
  if (status === "pending")
    return <Badge className="bg-amber-500 text-white">pending</Badge>;
  return <Badge variant="outline">none</Badge>;
};

const reviewBadge = (status) => {
  if (status === "approved")
    return <Badge className="bg-emerald-600 text-white">approved</Badge>;
  if (status === "rejected")
    return <Badge className="bg-rose-600 text-white">rejected</Badge>;
  return <Badge variant="outline">pending</Badge>;
};

const formatTco2e = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(2);
};

const BankCarbonSubmissionsPage = observer(() => {
  const {
    page,
    pageSize,
    total,
    setPage,
    setPageSize,
    carbonSubmissionsData,
    carbonOverviewData,
    loadCarbonBankSubmissions,
    loadCarbonBankOverview,
    filterOptions,
    setFilter,
  } = useCarbonBankSubmissionsStore();
  const { fetchHierarchy, tree, loading: orgLoading } = useOrganizationStore();
  const { t } = useTran();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [totalAccessibleOrgCount, setTotalAccessibleOrgCount] = useState(0);
  const [accessibleOrgIds, setAccessibleOrgIds] = useState([]);
  const [treeWidth, setTreeWidth] = useState(() => {
    const saved = localStorage.getItem("nxstart_carbon_tree_width");
    return saved ? Number(saved) : 280;
  });
  const yearOptions = useMemo(() => generateYearOptions(), []);

  const StatusOptions = [
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "pending", label: "Pending" },
  ];

  const loginOrgId =
    user?.organizationUnitId ||
    user?.organizationUnit?.id ||
    user?.organization?.id ||
    null;

  // Fetch organization hierarchy on mount
  useEffect(() => {
    if (orgLoading || tree.length) return;
    fetchHierarchy({ selectId: loginOrgId }).catch(() => {});
  }, [fetchHierarchy, loginOrgId, tree.length]);

  //   Calculate submission counts per organization unit
  const submissionCounts = useMemo(() => {
    return carbonSubmissionsData.reduce((acc, sub) => {
      const orgId = sub.organizationUnit?.id;
      if (orgId) acc[orgId] = (acc[orgId] || 0) + 1;
      return acc;
    }, {});
  }, [carbonSubmissionsData, selectedOrgIds]);

  // Filter submissions by selected org unit
  const filteredSubmissions = useMemo(() => {
    if (!selectedOrgIds?.length) return [];
    const selectedSet = new Set(selectedOrgIds);
    return carbonSubmissionsData.filter((sub) =>
      selectedSet.has(sub.organizationUnit?.id),
    );
  }, [carbonSubmissionsData, selectedOrgIds]);

  // Handler for org selection
  const handleOrgSelect = (orgId) => {
    setSelectedOrgIds((prev) => {
      if (prev.includes(orgId)) {
        return prev.filter((id) => id !== orgId); // remove
      } else {
        return [...prev, orgId]; // add
      }
    });
  };

  const totals = carbonOverviewData?.aggregated || {};

  const overviewYear =
    carbonOverviewData?.reportingYear ??
    (yearFilter && yearFilter !== "all" ? Number(yearFilter) : null);
  const overviewLabel =
    yearFilter === "all"
      ? "All years"
      : overviewYear
        ? formatYear(overviewYear)
        : "—";

  // Get selected org name for display
  // const selectedOrgName = selectedOrgId ? organizationStore.unitsById.get(selectedOrgId)?.name : null;
  const selectedOrgName = null;

  useEffect(() => {
    loadCarbonBankSubmissions();
    loadCarbonBankOverview();
  }, [filterOptions.year, filterOptions.status]);

  useEffect(() => {
    if (!tree?.[0]?.children?.length) return;
    const allOrgIds = tree[0].children.map((org) => org.id);
    setSelectedOrgIds(allOrgIds);
    setTotalAccessibleOrgCount(allOrgIds.length);
    setAccessibleOrgIds(allOrgIds);
  }, [tree]);

  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  const paginatedSubmissions = filteredSubmissions.slice(start, end);

  return (
    <main className="p-6">
      <div className="mx-auto space-y-4" style={{ maxWidth: "1400px" }}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              {t("SME Survey Submissions")}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t(
                "Review SME survey submissions and request acknowledgement/consent.",
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(PATHS.CARBON_DASHBOARD)}
              className="gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              {t("View Dashboard")}
            </Button>
          </div>
        </div>

        {/* Main layout with sidebar + content */}
        <div className="flex flex-col gap-4 md:flex-row">
          {/* Organization Tree Sidebar */}
          <div
            className="flex-shrink-0 rounded-lg"
            style={{ width: treeWidth, maxHeight: "fit-content" }}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-radius border border-border bg-card px-3 py-2">
              <Badge variant="outline">
                {t("Selected")}: {selectedOrgIds.length}/
                {totalAccessibleOrgCount}
              </Badge>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedOrgIds(accessibleOrgIds)}
                  disabled={totalAccessibleOrgCount === 0}
                >
                  {t("Select All")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedOrgIds([])}
                  disabled={selectedOrgIds.length === 0}
                >
                  {t("Clear")}
                </Button>
              </div>
            </div>
            <OrgFlatChecklist
              organizations={tree[0]?.children}
              selectedOrgIds={selectedOrgIds}
              countById={submissionCounts}
              toggleSelectedOrgId={handleOrgSelect}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card>
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base">
                  {selectedOrgName
                    ? `${selectedOrgName} (${overviewLabel})`
                    : `Consolidated view (${overviewLabel})`}
                </CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Select
                    value={filterOptions?.year}
                    onValueChange={(e) => setFilter("year", e)}
                  >
                    <SelectTrigger className="h-9 w-[180px]">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("All years")}</SelectItem>
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={filterOptions.status}
                    onValueChange={(e) => setFilter("status", e)}
                  >
                    <SelectTrigger className="h-9 w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("All statuses")}</SelectItem>
                      {StatusOptions.map((y) => (
                        <SelectItem key={y.key} value={y.key}>
                          {y.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("Approved Scope 1 total (tCO₂e)")}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                      {!overviewLoading &&
                      Number.isFinite(totals.scope1TotalTco2e)
                        ? totals.scope1TotalTco2e.toFixed(2)
                        : "—"}
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("Approved Scope 2 total (tCO₂e)")}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                      {!overviewLoading &&
                      Number.isFinite(totals.scope2TotalTco2e)
                        ? totals.scope2TotalTco2e.toFixed(2)
                        : "—"}
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("Submissions in filter")}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                      {loading
                        ? "—"
                        : filteredSubmissions.length.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {t(
                    "Totals include only bank-approved + consent-approved submissions.",
                  )}
                </div>

                <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
                      <tr>
                        <th className="px-4 py-3">SME</th>
                        <th className="px-4 py-3">Year</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Bank review</th>
                        {/* <th className="px-4 py-3">Consent</th> */}
                        <th className="px-4 py-3">Scope 1 total (tCO₂e)</th>
                        <th className="px-4 py-3">Scope 2 total (tCO₂e)</th>
                        <th className="px-4 py-3">Updated</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubmissions.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-slate-200 dark:border-slate-800"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                            {item.organizationUnit?.name || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                            {item.reportingYear}
                          </td>
                          <td className="px-4 py-3">
                            {item.status === "submitted" ? (
                              <Badge className="bg-emerald-600 text-white">
                                submitted
                              </Badge>
                            ) : (
                              <Badge variant="outline">draft</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {reviewBadge(item.bankReview?.status)}
                          </td>
                          {/* <td className="px-4 py-3">{consentBadge(item.consent?.status)}</td> */}
                          {(() => {
                            const usable =
                              item.bankReview?.status === "approved" &&
                              item.consent?.status === "approved";
                            const cls = usable
                              ? "text-slate-900 dark:text-white"
                              : "text-slate-500 dark:text-slate-400";
                            return (
                              <>
                                <td className={`px-4 py-3 ${cls}`}>
                                  {formatTco2e(item.totals?.scope1TotalTco2e)}
                                </td>
                                <td className={`px-4 py-3 ${cls}`}>
                                  {formatTco2e(item.totals?.scope2TotalTco2e)}
                                </td>
                              </>
                            );
                          })()}
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {item.updatedAt
                              ? new Date(item.updatedAt).toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate(
                                  PATHS.CARBON_SUBMISSIONS_EDIT.replace(
                                    ":id",
                                    item?.id,
                                  ),
                                )
                              }
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredSubmissions.length === 0 ? (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-slate-500"
                            colSpan={9}
                          >
                            {selectedOrgIds
                              ? "No submissions found for selected organization unit."
                              : "No submissions found."}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={(nextPage) => setPage(nextPage)}
                  onPageSizeChange={(e) => setPageSize(e)}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
});

export default BankCarbonSubmissionsPage;
